import { Controller } from "@hotwired/stimulus"
import { STYLE, loadMapLibre } from "../lib/maplibre"

// One place on a map: the "Where" block of an offer page, the sheet behind a
// place on a creator's page. It replaced OpenStreetMap's <iframe> embed, whose
// raster tiles and site chrome sat oddly beside the vector map a creator's
// page already draws — this is that map, at street level, with a single pin.
//
// `lazy` is for the sheet: the modal is display:none until it opens, and
// MapLibre reads a hidden container as 0×0 and paints nothing. The modal fires
// `show` once the box is measurable, and the map is built then — a page that
// lists a dozen places should not pay for a dozen maps nobody opened.
export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    // Street level: the block and the ones around it, enough to say which
    // corner of the neighbourhood this is without reading the address.
    zoom: { type: Number, default: 15 },
    lazy: { type: Boolean, default: false },
  }

  connect() {
    if (!this.lazyValue) this.show()
  }

  disconnect() {
    this.map?.remove()
    this.map = null
  }

  async show() {
    if (this.map) { this.map.resize(); return }
    // Two calls racing the import (open, close, open) would build two maps
    // into one box.
    if (this.building) return
    this.building = true

    const { Map, Marker, NavigationControl } = await loadMapLibre()
    // Stimulus can disconnect us while that import is in flight — a Turbo
    // navigation away from the page. Building into a detached element would
    // leak it: disconnect() has already run and will not run again.
    if (!this.element.isConnected) { this.building = false; return }

    const center = [this.lngValue, this.latValue]
    this.map = new Map({
      container: this.element,
      style: STYLE,
      center,
      zoom: this.zoomValue,
      attributionControl: { compact: true },
      // A map in a column of text, or in a scrolling sheet: grabbing the wheel
      // to zoom a map the visitor was only scrolling past is the classic
      // embedded-map sin.
      scrollZoom: false,
      // Neither is the map the point of the page.
      dragRotate: false,
      touchPitch: false,
    })
    this.map.addControl(new NavigationControl({ showCompass: false }), "top-right")
    // MapLibre opens the compact attribution once the style's sources have
    // named themselves — not at construction, when it is still empty — and
    // folds it on the first drag. Nobody drags a 200px box in a sheet, and
    // open it covers a third of the map on two lines: fold it once the tiles
    // are in. The ⓘ stays, and a tap unfolds it.
    this.map.once("load", () =>
      this.element.querySelector(".maplibregl-ctrl-attrib")?.classList.remove("maplibregl-compact-show"))

    // A wrapper around the pin, never the pin itself — MapLibre positions a
    // marker with `transform: translate(…)` on the element it is handed, and
    // any transform of the pin's own would compose with it. See .wc-pin.
    const pin = document.createElement("div")
    pin.className = "place-pin"
    const anchor = document.createElement("div")
    anchor.appendChild(pin)
    new Marker({ element: anchor }).setLngLat(center).addTo(this.map)
    this.building = false
  }
}
